// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Paper } from '@material-ui/core';
import { Box, useSystem } from 'react-system';
import { minBy } from '../../utils/array';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { LoadingButton } from '../../controls/loading-button';
import { LotPrice, LotPricePerMeterSquared } from '../lot-price';
import { useAnalytics } from '../../controls/analytics';
import { useSubmitRegistration } from './useSubmitRegistration';
import { DevelopmentEnquiry } from './DevelopmentEnquiry';
import type { DevelopmentActionCard_development$key } from './__generated__/DevelopmentActionCard_development.graphql';

type Props = {|
  development: DevelopmentActionCard_development$key,
|};

export const DevelopmentActionCard = (props: Props): React.Node => {
  const { text } = useTheme();
  const { t } = useLocale();
  const { track } = useAnalytics();
  const { media } = useSystem();
  const [submitRegistration] = useSubmitRegistration();
  const [loadingRegistration, setRegistrationLoading] = React.useState(false);

  const development = useFragment(
    graphql`
      fragment DevelopmentActionCard_development on DevelopmentProduct {
        ...DevelopmentEnquiry_development
        id
        currentUserRegistrationForm {
          id
          completed
        }
        registrationType
        products {
          ...lotPrice_product
          ...lotPricePerMeterSquared_product
          salePrice
        }
      }
    `,
    props.development,
  );

  const products = development.products ?? [];

  const cheapestProduct = minBy(
    products,
    item => item.salePrice ?? Number.POSITIVE_INFINITY,
  );

  const registrationComplete =
    development.currentUserRegistrationForm?.completed;
  const registrationId = development.currentUserRegistrationForm?.id;
  let registrationButtonLabel = t('registration');
  if (registrationComplete === true) {
    registrationButtonLabel = t('viewRegistration');
  } else if (registrationId != null) {
    registrationButtonLabel = t('continueRegistration');
  }
  return (
    <Box css={media({ display: ['none', 'block'] })}>
      <Paper elevation={24}>
        <Box p={3}>
          <p css={{ ...text.body1, fontWeight: 'lighter' }}>
            {t('pricesStartAt')}
          </p>
          {cheapestProduct && (
            <h1 css={media([text.h5, text.h5, text.h4])}>
              <LotPrice product={cheapestProduct} />
            </h1>
          )}
          {cheapestProduct && (
            <p css={text.body2}>
              <LotPricePerMeterSquared product={cheapestProduct} />
            </p>
          )}
          {development.registrationType === 'complete_registration' && (
            <Box mt={4}>
              <LoadingButton
                color="primary"
                size="medium"
                loading={loadingRegistration}
                onClick={() => {
                  setRegistrationLoading(true);
                  track('RA.Web.Development.Register.Click', {
                    developmentId: development.id,
                  });
                  submitRegistration({
                    id: registrationId,
                    developmentProductId: development.id,
                  });
                }}
              >
                {registrationButtonLabel}
              </LoadingButton>
            </Box>
          )}

          {development.registrationType === 'normal_enquiry' && (
            <Box mt={4}>
              <DevelopmentEnquiry development={development} />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
